import { useCallback, useEffect, useState } from "react";
import { isProjectSearchShortcut } from "../lib/projectSearch.ts";
import type { Project } from "@racler/protocol";
import type { OpenFiles } from "../lib/useOpenFiles.ts";
import type { IdeLayout } from "../lib/useIdeLayout.ts";
import { useChat } from "../lib/useChat.ts";
import { useMediaQuery } from "../lib/useMediaQuery.ts";
import { useReview } from "../lib/useReview.ts";
import { AgentPane } from "./AgentPane.tsx";
import { BottomPanel } from "./BottomPanel.tsx";
import { EditorArea } from "./EditorArea.tsx";
import { Explorer } from "./Explorer.tsx";
import { HistoryPanel } from "./HistoryPanel.tsx";
import { NarrowIde, type NarrowView } from "./NarrowIde.tsx";
import { HostPortal, useHost } from "./PanelHost.tsx";
import { ProjectSearch } from "./ProjectSearch.tsx";
import { ReviewHost } from "./ReviewHost.tsx";
import { WideIde } from "./WideIde.tsx";

export interface Props {
  machineId: string;
  project: Project;
  files: OpenFiles;
  layout: IdeLayout;
  chatId?: string | undefined;
}

/**
 * IDE shell. The content of every panel (Files, Code, Tools, Agent, History) is rendered ONCE, here, through a portal
 * into a stable host element (see PanelHost). The wide layout and the narrow layout only decide where those hosts are
 * shown, so hiding a panel, docking it, or crossing the tablet/phone breakpoint never unmounts the editor (tabs,
 * unsaved edits, Monaco), the terminals, the chat or the Explorer tree.
 *
 * Chat state lives here, once, and is shared by the Agent panel and the History panel (embedded or standalone).
 */
export function Ide({ machineId, project, files: openFiles, layout, chatId }: Props) {
  const narrow = useMediaQuery("(max-width: 800px)");
  const [view, setView] = useState<NarrowView>("agent");
  const review = useReview();
  const controller = useChat(machineId, project.id, chatId);
  const hosts = {
    files: useHost("files"),
    code: useHost("code"),
    tools: useHost("tools"),
    agent: useHost("agent"),
    history: useHost("history"),
  };

  // Opening a file while the editor is hidden brings it back (nothing was lost while hidden).
  const { show } = layout;
  const showCode = useCallback(() => (narrow ? setView("code") : show("code")), [narrow, show]);
  const files: OpenFiles = {
    ...openFiles,
    open: (p) => {
      showCode();
      openFiles.open(p);
    },
  };

  // Global Project Search (Cmd/Ctrl+Shift+F): one modal; pressing the shortcut again re-focuses it.
  const [search, setSearch] = useState<{ open: boolean; nonce: number }>({ open: false, nonce: 0 });
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!isProjectSearchShortcut(e)) return;
      e.preventDefault();
      e.stopPropagation();
      setSearch((s) => ({ open: true, nonce: s.nonce + 1 }));
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, []);
  const closeSearch = useCallback(() => setSearch((s) => ({ ...s, open: false })), []);
  const navigateFromSearch = (path: string, range: Parameters<OpenFiles["reveal"]>[1] | null) => {
    showCode();
    if (range) openFiles.reveal(path, range);
    else openFiles.open(path);
  };

  const { toggle } = layout;
  useEffect(() => {
    if (narrow) return;
    const onKey = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.altKey || e.shiftKey) return;
      if (e.key === "j") {
        e.preventDefault();
        toggle("tools");
      } else if (e.key === "b") {
        e.preventDefault();
        toggle("files");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [narrow, toggle]);

  const historyStandalone = !narrow && layout.model.history.standalone;

  return (
    <>
      {search.open && (
        <ProjectSearch
          machineId={machineId}
          projectId={project.id}
          focusNonce={search.nonce}
          onClose={closeSearch}
          onNavigate={navigateFromSearch}
        />
      )}
      <HostPortal host={hosts.files}>
        <Explorer
          machineId={machineId}
          projectId={project.id}
          projectName={project.name}
          files={files}
          externalChange={review.fsChange}
        />
      </HostPortal>
      <HostPortal host={hosts.code}>
        <ReviewHost machineId={machineId} files={files} review={review}>
          <EditorArea files={files} />
        </ReviewHost>
      </HostPortal>
      <HostPortal host={hosts.tools}>
        <BottomPanel
          machineId={machineId}
          projectId={project.id}
          {...(narrow ? {} : { onHide: () => layout.hide("tools") })}
        />
      </HostPortal>
      <HostPortal host={hosts.agent}>
        <AgentPane
          machineId={machineId}
          projectId={project.id}
          files={files}
          controller={controller}
          review={review}
          historyStandalone={historyStandalone}
        />
      </HostPortal>
      {/* Unlike Code / Tools / Files / Agent, History holds no state of its own (it renders the shared chat
          controller), so it is mounted only while it is shown. Embedded History lives in the Agent panel. */}
      {(historyStandalone || (narrow && view === "history")) && (
        <HostPortal host={hosts.history}>
          <HistoryPanel controller={controller} {...(narrow ? { onPicked: () => setView("agent") } : {})} />
        </HostPortal>
      )}
      {narrow ? (
        <NarrowIde hosts={hosts} view={view} onView={setView} />
      ) : (
        <WideIde hosts={hosts} layout={layout} />
      )}
    </>
  );
}

export type IdeHosts = {
  files: HTMLDivElement;
  code: HTMLDivElement;
  tools: HTMLDivElement;
  agent: HTMLDivElement;
  history: HTMLDivElement;
};
